import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { track, EVENTS } from "@/lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

export default function QuizEngine({ decoder, onComplete }) {
    const total = decoder.questions.length;
    const [index, setIndex] = useState(0);
    const [picked, setPicked] = useState(null);
    const [answers, setAnswers] = useState([]);

    const q = decoder.questions[index];

    const choose = (option) => {
        if (picked) return;
        setPicked(option.id);
        track(EVENTS.QUESTION_ANSWERED, {
            decoder: decoder.id,
            question: q.id,
            option: option.id,
        });
        const next = [...answers.slice(0, index), option];
        setAnswers(next);
        setTimeout(() => {
            if (index + 1 < total) {
                setIndex(index + 1);
                setPicked(null);
            } else {
                onComplete(next);
            }
        }, 340);
    };

    const goBack = () => {
        if (index === 0) return;
        setIndex(index - 1);
        setPicked(answers[index - 1] ? answers[index - 1].id : null);
    };

    return (
        <div className="flex flex-1 flex-col">
            <header className="sticky top-0 z-20 -mx-5 mb-6 bg-ink/80 px-5 pt-5 pb-3 backdrop-blur-sm">
                <div className="mb-2 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={index === 0}
                        data-testid="quiz-back-button"
                        className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-slate-200 disabled:opacity-30"
                    >
                        ← Back
                    </button>
                    <p
                        data-testid="quiz-progress"
                        className="font-mono text-[11px] uppercase tracking-[0.3em] text-celestial-blue"
                    >
                        {index + 1} of {total}
                    </p>
                </div>
                <div className="h-px w-full bg-white/10">
                    <div
                        className="h-px bg-celestial-gold transition-all duration-500 ease-out"
                        style={{ width: `${((index + 1) / total) * 100}%` }}
                    />
                </div>
            </header>

            <div className="flex flex-1 flex-col justify-center pb-6">
                <AnimatePresence mode="wait">
                    <motion.fieldset
                        key={q.id}
                        initial={{ opacity: 0, x: 32 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -32 }}
                        transition={{ duration: 0.35, ease: EASE }}
                    >
                        <legend className="sr-only">
                            Question {index + 1} — pick the answer that sounds
                            most like your first instinct
                        </legend>
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
                            Situation {String(index + 1).padStart(2, "0")}
                        </p>
                        <h1
                            data-testid="quiz-scenario"
                            aria-live="polite"
                            className="mt-3 whitespace-pre-line font-display text-[clamp(1.6rem,6.5vw,2.3rem)] leading-snug text-slate-100"
                        >
                            {q.scenario}
                        </h1>
                        <p className="mt-4 font-sans text-sm font-bold uppercase tracking-[0.12em] text-celestial-gold">
                            {q.prompt}
                        </p>

                        <div className="mt-6 flex flex-col gap-3">
                            {q.options.map((opt, i) => {
                                const active = picked === opt.id;
                                return (
                                    <label
                                        key={opt.id}
                                        className={`flex min-h-[64px] cursor-pointer items-center gap-4 rounded-2xl border px-5 py-4 transition-all duration-200 ${
                                            active
                                                ? "border-celestial-gold bg-celestial-gold/10 shadow-[0_0_30px_rgba(217,179,108,0.12)]"
                                                : "border-white/10 bg-white/[0.03] hover:border-celestial-blue/50 hover:bg-white/[0.06] active:scale-[0.99]"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name={`q-${q.id}`}
                                            value={opt.id}
                                            checked={active}
                                            disabled={!!picked}
                                            onChange={() => choose(opt)}
                                            data-testid={`quiz-option-${opt.id}`}
                                            className="sr-only"
                                        />
                                        <span
                                            className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border font-mono text-[10px] ${
                                                active
                                                    ? "border-celestial-gold text-celestial-gold"
                                                    : "border-white/20 text-slate-500"
                                            }`}
                                        >
                                            {String.fromCharCode(65 + i)}
                                        </span>
                                        <span className="whitespace-pre-line text-[15px] leading-snug text-slate-100 sm:text-base">
                                            {opt.label}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </motion.fieldset>
                </AnimatePresence>
            </div>
        </div>
    );
}
